import { bbox } from '@turf/turf';
import type * as MapGL from 'react-map-gl/mapbox';
import { proxy } from 'valtio';
import type { ProjectIntelligenceResponse } from '~/api/projectsV3';
import type { Project, ProjectConflict } from '~/common/types';
import { IntelligenceSectionTitle, type IntelligenceV2 } from './common';

type F = { [key: string]: GeoJSON.Feature };

class Store {
  /** project is only for the legacy components.
   * New components should use Tanstack Query. */
  project: Project | null = null;

  isLoading: boolean = false;
  isError: boolean = false;

  intelligenceFeatures: { [key: string]: IntelligenceV2 } = {};
  config: { [key: string]: { [key: string]: GeoJSON.Feature } } = {};

  citygrid: F = {};

  hoveredFeature: GeoJSON.Feature | null = null;
  selectedFeature: GeoJSON.Feature | null = null;

  activeSection: IntelligenceSectionTitle = IntelligenceSectionTitle.NearbyProjects;

  map?: MapGL.MapRef = undefined;

  setProject(project: Project) {
    this.project = project;
  }

  setProjectIntelligence(projectIntelligence: ProjectIntelligenceResponse) {
    Object.entries(projectIntelligence.intelligenceItems ?? {}).forEach(([layerId, items]) => {
      if (!this.config[layerId]) {
        this.config[layerId] = {};
      }

      items.forEach((item) => {
        this.config[layerId][item.featureId] = {
          type: 'Feature',
          id: item.featureId,
          geometry: item.geometry,
          properties: {
            ...item.properties,
            overlap: item.calculations?.boundaryOverlapPercentage,
          },
        };

        this.intelligenceFeatures[item.featureId] = item as unknown as IntelligenceV2;
      });
    });

    (projectIntelligence.projectConflicts ?? []).forEach((conflict) => {
      if (!this.config[conflict.layerId]) {
        this.config[conflict.layerId] = {};
      }

      this.config[conflict.layerId][conflict.featureId] = this.toConflictFeature(conflict);
    });
  }

  /** Builds the GeoJSON feature for a conflict row and registers its color in `intelligenceFeatures`. */
  private toConflictFeature(conflict: ProjectConflict): GeoJSON.Feature {
    const feature: GeoJSON.Feature = {
      type: 'Feature',
      id: conflict.featureId,
      geometry: conflict.geometry,
      properties: conflict.details,
    };

    if (!this.intelligenceFeatures[conflict.featureId]) {
      this.intelligenceFeatures[conflict.featureId] = {
        id: conflict.id,
        layerId: conflict.layerId,
        rgb: conflict.rgb,
        geometry: conflict.geometry,
        properties: conflict.details,
      } as unknown as IntelligenceV2;
    }

    return feature;
  }

  selectConflict(conflict: ProjectConflict) {
    const feature = this.toConflictFeature(conflict);
    this.setSelectedFeature(feature);
    this.zoomToFeature(feature);
  }

  hoverConflict(conflict: ProjectConflict) {
    this.setHoveredFeature(this.toConflictFeature(conflict));
  }

  setHoveredFeature(feature: GeoJSON.Feature) {
    this.hoveredFeature = feature;
  }

  resetHoveredFeature() {
    this.hoveredFeature = null;
  }

  setSelectedFeature(feature: GeoJSON.Feature) {
    if (this.hoveredFeature) {
      this.hoveredFeature = null;
    }

    this.selectedFeature = feature;
  }

  resetSelectedFeature() {
    this.selectedFeature = null;
  }

  private resetFields() {
    this.isLoading = true;
    this.isError = false;
    this.intelligenceFeatures = {};
    this.config = {};
    this.citygrid = {};
    this.project = null;
    this.hoveredFeature = null;
    this.selectedFeature = null;
    this.activeSection = IntelligenceSectionTitle.NearbyProjects;
    this.config = {};
  }

  reset() {
    this.resetFields();
  }

  setCurrentMap(map?: MapGL.MapRef) {
    this.map = map;
  }

  resetCurrentMap() {
    this.map = undefined;
  }

  zoomToFeature(f: GeoJSON.Feature) {
    if (this.map) {
      const box = bbox(f) as [number, number, number, number];
      this.map.fitBounds(box, { padding: 100, speed: 2 });
    }
  }
}

const store = proxy(new Store());
export default store;
